import React,{useState,useEffect} from 'react';
import {useParams,useNavigate,Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useData} from '../contexts/DataContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft,FiSave,FiUser,FiMail,FiPhone,FiMap,FiCalendar,FiPackage,FiVideo,FiPlus}=FiIcons;

const EditMember=()=> {
  const {memberId}=useParams();
  const navigate=useNavigate();
  const {members,updateMember,loading: dataLoading}=useData();
  const [formData,setFormData]=useState({
    name: '',
    email: '',
    country: '',
    state: '',
    city: '',
    phone: '',
    teams_id: '',
    programs: [],
    date_joined: ''
  });
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');
  const [initialLoading,setInitialLoading]=useState(true);
  const [customProgram,setCustomProgram]=useState('');

  const programOptions=[
    'Amazon FBA/FBM',
    'Amazon White Label',
    'Mindset/Accountability',
    'SaaS',
    'AI',
    'Affiliate Marketing',
    'Social Media',
    'Facebook Groups',
    'Strategy',
    'eFormula',
    'BlackBox',
    'Commission Blueprint',
    'Omega',
    '123 Profit',
    '7 Day Empires'
  ];

  // Load member data when component mounts
  useEffect(()=> {
    const member=members.find(m=> m.id.toString()===memberId);
    if (member) {
      setFormData({
        name: member.name || '',
        email: member.email || '',
        country: member.country || '',
        state: member.state || '',
        city: member.city || '',
        phone: member.phone || '',
        teams_id: member.teams_id || '',
        programs: member.programs || [],
        date_joined: member.date_joined ? new Date(member.date_joined).toISOString().split('T')[0] : ''
      });
      setInitialLoading(false);
    } else if (!dataLoading) {
      setError('Member not found');
      setInitialLoading(false);
    }
  },[memberId,members,dataLoading]);

  const handleSubmit=async (e)=> {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Convert date_joined to ISO string
      const memberData={
        ...formData,
        id: memberId,
        date_joined: new Date(formData.date_joined).toISOString(),
        programs: formData.programs.length > 0 ? formData.programs : null
      };

      await updateMember(memberData);
      navigate(`/members/${memberId}`);
    } catch (err) {
      setError(err.message || 'Failed to update member. Please try again.');
      console.error('Error updating member:',err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange=(e)=> {
    const {name,value}=e.target;
    setFormData(prev=> ({
      ...prev,
      [name]: value
    }));
  };

  const handleProgramToggle=(program)=> {
    setFormData(prev=> {
      const currentPrograms=[...prev.programs];
      if (currentPrograms.includes(program)) {
        return {
          ...prev,
          programs: currentPrograms.filter(p=> p !==program)
        };
      } else {
        return {
          ...prev,
          programs: [...currentPrograms,program]
        };
      }
    });
  };

  const handleAddCustomProgram=()=> {
    if (customProgram.trim() && !formData.programs.includes(customProgram.trim())) {
      setFormData(prev=> ({
        ...prev,
        programs: [...prev.programs,customProgram.trim()]
      }));
      setCustomProgram('');
    }
  };

  const handleCustomProgramKeyPress=(e)=> {
    if (e.key==='Enter') {
      e.preventDefault();
      handleAddCustomProgram();
    }
  };

  const removeProgram=(programToRemove)=> {
    setFormData(prev=> ({
      ...prev,
      programs: prev.programs.filter(p=> p !==programToRemove)
    }));
  };

  if (initialLoading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error==='Member not found') {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Member not found</p>
          <Link to="/members" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
            ← Back to Members
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        className="mb-8"
      >
        <div className="flex items-center space-x-4 mb-4">
          <Link
            to={`/members/${memberId}`}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back to Member</span>
          </Link>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Member</h1>
        <p className="text-gray-600">Update member information</p>
      </motion.div>

      <motion.div
        initial={{opacity: 0,y: 20}}
        animate={{opacity: 1,y: 0}}
        transition={{delay: 0.1}}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
      >
        {error && error !=='Member not found' && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiUser} className="w-4 h-4 inline mr-2" />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter member's full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiMail} className="w-4 h-4 inline mr-2" />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter member's email address"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiPhone} className="w-4 h-4 inline mr-2" />
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter member's phone number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiVideo} className="w-4 h-4 inline mr-2" />
                    Microsoft Teams ID (optional)
                  </label>
                  <input
                    type="text"
                    name="teams_id"
                    value={formData.teams_id}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter member's Teams ID (optional)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If left empty,the member's email will be used for Teams links
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4 inline mr-2" />
                    Date Joined
                  </label>
                  <input
                    type="date"
                    name="date_joined"
                    value={formData.date_joined}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Location & Programs */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Location & Programs</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiMap} className="w-4 h-4 inline mr-2" />
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter member's country"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiMap} className="w-4 h-4 inline mr-2" />
                    State/Province/Region
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter member's state or region"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiMap} className="w-4 h-4 inline mr-2" />
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter member's city"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiPackage} className="w-4 h-4 inline mr-2" />
                    Programs
                  </label>
                  
                  {/* Predefined Programs */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {programOptions.map(program=> (
                      <div
                        key={program}
                        onClick={()=> handleProgramToggle(program)}
                        className={`px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                          formData.programs.includes(program)
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                        }`}
                      >
                        {program}
                      </div>
                    ))}
                  </div>

                  {/* Custom Program Input */}
                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Custom Program
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={customProgram}
                        onChange={(e)=> setCustomProgram(e.target.value)}
                        onKeyPress={handleCustomProgramKeyPress}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        placeholder="Enter custom program name"
                      />
                      <button
                        type="button"
                        onClick={handleAddCustomProgram}
                        disabled={!customProgram.trim()}
                        className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Press Enter or click + to add a custom program
                    </p>
                  </div>

                  {/* Selected Programs Display */}
                  {formData.programs.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Selected Programs:</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.programs.map(program=> (
                          <span
                            key={program}
                            className="inline-flex items-center px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full"
                          >
                            {program}
                            <button
                              type="button"
                              onClick={()=> removeProgram(program)}
                              className="ml-2 text-primary-600 hover:text-primary-800"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <Link
              to={`/members/${memberId}`}
              className="px-6 py-3 text-gray-700 hover:text-gray-900 font-medium transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditMember;